import React, { useState, useEffect } from "react";
import axios from "axios";
import { Pill, MapPin, Phone, Plus, Trash2, LogOut, Moon } from "lucide-react";
import { clearLoggedIn } from "../lib/authState";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Espace du titulaire d'une pharmacie — DEDIE et volontairement le plus
// simple des tableaux de bord d'etablissement (voir routes/officine.py,
// get_pharmacy_establishment_context) : une pharmacie de ville n'a ni
// personnel ni patients ni rendez-vous, donc pas de systeme a onglets/
// modules comme HopitalDashboard.js/LaboDashboard.js — juste la fiche
// publique (annuaire) et les jours de garde, un seul fichier autonome.
const getAuthConfig = () => ({
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

const inputStyle = "w-full border border-[#E2E8F0] rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0596DE] bg-white";

const PharmacyDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [establishment, setEstablishment] = useState(null);
  const [wilayas, setWilayas] = useState([]);
  const [form, setForm] = useState({ phone: "", address: "", wilaya_code: "", daira: "", commune: "", description: "" });
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [gardeDates, setGardeDates] = useState([]);
  const [newDate, setNewDate] = useState("");
  const [gardeError, setGardeError] = useState("");

  const load = () => {
    setLoading(true);
    axios.get(`${API_URL}/api/wilayas`).then((r) => setWilayas(r.data || [])).catch(() => {});
    axios.get(`${API_URL}/api/officine/me`, getAuthConfig())
      .then((r) => {
        const est = r.data.establishment || {};
        setEstablishment(est);
        setForm({
          phone: est.phone || "", address: est.address || "", wilaya_code: est.wilaya_code || "",
          daira: est.daira || "", commune: est.commune || "", description: est.description || "",
        });
      })
      .catch((e) => { if (e.response?.status === 403) setForbidden(true); })
      .finally(() => setLoading(false));
    axios.get(`${API_URL}/api/officine/garde`, getAuthConfig())
      .then((r) => setGardeDates(r.data.garde_dates || []))
      .catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true); setError(""); setNotice("");
    try {
      await axios.put(`${API_URL}/api/officine/profile`, form, getAuthConfig());
      setNotice("Fiche mise à jour. Elle est visible sur votre page publique.");
    } catch (e) {
      setError(e.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const addGarde = async (e) => {
    e.preventDefault();
    if (!newDate) return;
    setGardeError("");
    try {
      const r = await axios.post(`${API_URL}/api/officine/garde`, { date: newDate }, getAuthConfig());
      setNewDate("");
      if (!gardeDates.some((g) => g.date === r.data.garde.date)) {
        setGardeDates([...gardeDates, r.data.garde].sort((a, b) => a.date.localeCompare(b.date)));
      }
    } catch (e) {
      setGardeError(e.response?.data?.detail || "Erreur lors de l'ajout");
    }
  };

  const removeGarde = async (gardeId) => {
    try {
      await axios.delete(`${API_URL}/api/officine/garde/${gardeId}`, getAuthConfig());
      setGardeDates(gardeDates.filter((g) => g.garde_id !== gardeId));
    } catch (e) {
      setGardeError("Erreur lors de la suppression");
    }
  };

  const logout = async () => {
    try { await axios.post(`${API_URL}/api/auth/logout`, {}, getAuthConfig()); } catch (e) { /* on deconnecte quand meme */ }
    clearLoggedIn();
    window.location.href = "/login";
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="animate-spin w-10 h-10 border-4 border-[#0596DE] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (forbidden) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] px-4">
        <div className="max-w-md text-center bg-white rounded-2xl border border-[#E2E8F0] p-8">
          <Pill className="w-12 h-12 text-[#94A3B8] mx-auto mb-4" />
          <h1 className="text-xl font-bold text-[#0F172A] mb-2">Espace pharmacie indisponible</h1>
          <p className="text-[#64748B] mb-6">
            Votre demande d'inscription est peut-être encore en attente de validation par notre équipe (48h en général),
            ou vous n'êtes pas connecté avec un compte pharmacie.
          </p>
          <button onClick={logout} className="text-sm text-[#A32D2D]">Se déconnecter</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <div className="bg-white border-b border-[#E2E8F0]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#E6F1FB] flex items-center justify-center text-[#0596DE]">
              <Pill className="w-5 h-5" />
            </div>
            <div>
              <div className="font-semibold text-[#0F172A]">{establishment?.name || "Ma pharmacie"}</div>
              <div className="text-xs text-[#64748B]">Espace pharmacie</div>
            </div>
          </div>
          <button onClick={logout} className="flex items-center gap-1.5 text-sm text-[#A32D2D]">
            <LogOut className="w-4 h-4" /> Se déconnecter
          </button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6">
          <h2 className="text-lg font-bold text-[#0F172A] mb-1 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-[#0596DE]" /> Ma fiche publique
          </h2>
          <p className="text-sm text-[#64748B] mb-4">Ces informations apparaissent sur l'annuaire des pharmacies.</p>

          {notice && <div className="bg-[#EAF3DE] border border-[#CDE3AE] text-[#3B6D11] rounded-lg px-4 py-2.5 text-sm mb-4">{notice}</div>}
          {error && <div className="bg-[#FCEBEB] border border-[#F7C1C1] text-[#A32D2D] rounded-lg px-4 py-2.5 text-sm mb-4">{error}</div>}

          <form onSubmit={saveProfile} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-[#334155] mb-1 block">Téléphone</label>
                <input className={inputStyle} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+213 XX XX XX XX" />
              </div>
              <div>
                <label className="text-sm text-[#334155] mb-1 block">Wilaya</label>
                <select className={inputStyle} value={form.wilaya_code} onChange={(e) => setForm({ ...form, wilaya_code: e.target.value })}>
                  <option value="">Sélectionner...</option>
                  {wilayas.map((w) => (
                    <option key={w.code} value={w.code}>{w.code} - {w.name_fr}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="text-sm text-[#334155] mb-1 block">Adresse</label>
              <input className={inputStyle} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Rue, quartier..." />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-[#334155] mb-1 block">Daïra</label>
                <input className={inputStyle} value={form.daira} onChange={(e) => setForm({ ...form, daira: e.target.value })} />
              </div>
              <div>
                <label className="text-sm text-[#334155] mb-1 block">Commune</label>
                <input className={inputStyle} value={form.commune} onChange={(e) => setForm({ ...form, commune: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="text-sm text-[#334155] mb-1 block">Description (optionnel)</label>
              <textarea className={`${inputStyle} min-h-[90px] resize-y`} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Présentez votre pharmacie..." />
            </div>
            <button type="submit" disabled={saving} className="rounded-xl bg-[#0596DE] hover:bg-[#047CBA] text-white font-semibold px-5 py-2.5 text-sm disabled:opacity-60">
              {saving ? "Enregistrement..." : "Enregistrer"}
            </button>
          </form>
        </div>

        <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6">
          <h2 className="text-lg font-bold text-[#0F172A] mb-1 flex items-center gap-2">
            <Moon className="w-5 h-5 text-[#0596DE]" /> Jours de garde
          </h2>
          <p className="text-sm text-[#64748B] mb-4">
            Déclarez vos jours (ou nuits) de garde : ils apparaissent immédiatement dans l'annuaire public avec le filtre "de garde aujourd'hui".
          </p>

          {gardeError && <div className="bg-[#FCEBEB] border border-[#F7C1C1] text-[#A32D2D] rounded-lg px-4 py-2.5 text-sm mb-4">{gardeError}</div>}

          <form onSubmit={addGarde} className="flex flex-col sm:flex-row gap-3 mb-4">
            <input
              type="date"
              className={`${inputStyle} sm:w-56`}
              value={newDate}
              onChange={(e) => setNewDate(e.target.value)}
              min={new Date().toISOString().slice(0, 10)}
            />
            <button type="submit" className="shrink-0 flex items-center justify-center gap-1.5 rounded-xl bg-[#0596DE] hover:bg-[#047CBA] text-white font-semibold px-4 py-2 text-sm">
              <Plus className="w-4 h-4" /> Ajouter
            </button>
          </form>

          {gardeDates.length === 0 ? (
            <p className="text-sm text-[#64748B]">Aucun jour de garde déclaré pour le moment.</p>
          ) : (
            <div className="space-y-2">
              {gardeDates.map((g) => (
                <div key={g.garde_id} className="flex items-center justify-between bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg px-4 py-2.5">
                  <span className="text-sm text-[#0F172A] font-medium">
                    {new Date(g.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                  </span>
                  <button onClick={() => removeGarde(g.garde_id)} className="text-[#A32D2D] hover:text-[#822323]" title="Retirer">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {establishment?.phone && (
          <p className="text-xs text-[#94A3B8] flex items-center gap-1.5">
            <Phone className="w-3.5 h-3.5" /> Besoin d'aide ? contact@tabbibak.com
          </p>
        )}
      </div>
    </div>
  );
};

export default PharmacyDashboard;
